/**
 * Base class for all context objects like commands and mediators
 * Extended by: AbstractMediator, AbstractCommand
 * 
 * Contains convenience methods for the following:
 * - managing context event listeners and dispatching context events
 * - retrieving text from language pack
 * - managing delayed calls
 * - opening popups
 * - playing sounds
 * - tracking analytics events
 * 
 * Created on 11-apr-2017, 12:23:57
 * @author Reinier Feijen for &ranj
 **/

import GoogleAnalyticsEvent from "../../analytics/GoogleAnalyticsEvent";
import SkinFactory from "../ui/SkinFactory";
import NavigateEvent from "../navigation/NavigateEvent";
import { resolveVariablesInString } from "../../../../lib/html5-software-library/src/ranj/util/StringUtil";
import PlatformAPIEventType from "../../equalture/platform/PlatformAPIEventType";
import LanguagePack from "../../../../lib/html5-software-library/src/ranj/language/LanguagePack";
import SkinKey from "../ui/SkinKey";
import AnimationOptions from "../../../../lib/html5-software-library/src/ranj/ui/animation/AnimationOptions";

export default class AbstractContextObject {

    /**
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LanguagePack} languagePack 
     */
    constructor(dispatcher, languagePack, soundManager, appModel) {
        this._dispatcher = dispatcher;
        this._languagePack = languagePack;
        this._soundManager = soundManager;
        this._appModel = appModel;
    }

    destroy() {
        //remove all registered listeners
        if (this.handlers && this._dispatcher) {
            for (var type in this.handlers) {
                for (var i = 0; i < this.handlers[type].length; i++) {
                    this._dispatcher.removeEventListener(type, this.handlers[type][i]);
                }
            }
        }
        //Kill all delayed calls
        this.killDelayedCalls();
    }



    dispatchPlatformProgressEvent(progressValue) {
        this.dispatch(PlatformAPIEventType.PROGRESS, { progressValue: progressValue });

    }

    /**
     * Convenience method for adding context listeners.
     * Listeners added with this method will be automatically removed in destroy()
     * Requires: this._dispatcher
     * @param {String} type
     * @param {Function} handler Bound handler!
     */
    registerEventListener(type, handler) {
        //store reference to handler so we can remove it in destroy()
        this._dispatcher.addEventListener(type, handler);
        var handlersForType = this._getHandlersForTtype(type);
        handlersForType.push(handler);
    }
    unregisterEventListener(type, handler) {
        this._dispatcher.removeEventListener(type, handler);
        var handlersForType = this._getHandlersForTtype(type);
        for (var i = handlersForType.length - 1; i >= 0; i--) {
            if (handlersForType[i] == handler) handlersForType.splice(i, 1);
        }
        if (!handlersForType.length) delete this.handlers[type];
    }
    _getHandlersForTtype(type) {
        if (!this.handlers) this.handlers = {};
        if (!this.handlers[type]) this.handlers[type] = [];
        return this.handlers[type];
    }
    /**
     * Convenience method for dispatching context events
     */
    dispatch(type, data) {
        this._dispatcher.dispatch(type, data);
    }


    /**
     * Convenience method for retrieving languagepack texts
     * Requires: this._languagePack
     * @param {String} id 
     * @returns {String} text from languagepack
     **/
    getText(id, variables = null) {
        let str = this._languagePack.getText(id);
        if (str != null && variables != null) {
            str = resolveVariablesInString(str, variables);
        }
        return str;
    }


    /**
     * Convenience method for opening popup
     * Uses: NavigateCommandEvent, SkinFactory
     * @param {string} skinType Identifier of skin, see SkinFactory
     * @param {Function} onClose Optional callback for closing popup
     * @returns {DisplayObject} popup
     */
    openPopup(skinType, onClose, animationType = null, animationOptions = null, skinData) {
        var popup = SkinFactory.getSkin(skinType);
        popup.onClose = onClose;
        popup.parameters = skinData;
        //Method signature of NavigateCommandEvent constructor:
        //function(type, screenType, screen, autoCenter, priority, hasOverlay)
        var commandEvent = new NavigateEvent(NavigateEvent.POP_UP_REQUEST, null, popup, undefined, undefined, false, undefined, animationType, animationOptions);
        this._dispatcher.dispatch(commandEvent.type, { data: commandEvent });

        return popup;
    }


    /**
     * Closes a popup
     * @param {SkinKey} skinKey Identifier of skin
     * @param {string} animationType
     * @param {*} animationOptions
     */
    closePopup(skin, animationType = null, animationOptions = null) {
        const commandEvent = new NavigateEvent(NavigateEvent.CLOSE_POP_UP_REQUEST, null, skin, null, null, null, null, animationType, animationOptions);
        this._dispatcher.dispatch(commandEvent.type, { data: commandEvent });
    }


    /**
     * Convenience method for delayed function calls
     * @param {Function} handler Bound handler
     * @param {Number} delay Delay in msecs
     * @param {Object} scope Optional scope ('this') to bind handler with. Default this.
     * @return {Object} Timeout id
     */
    callDelayed(callback, delay) {
        if (!this._delayedCalls) this._delayedCalls = [];
        const timeout = setTimeout(callback, delay);
        this._delayedCalls.push(timeout);
        return timeout;
    }
    killDelayedCalls() {
        if (!this._delayedCalls) return;
        for (var i = 0; i < this._delayedCalls.length; i++) {
            clearTimeout(this._delayedCalls[i]);
        }
        this._delayedCalls.length = 0;
    }
    killDelayedCall(timeout) {
        clearTimeout(timeout);
        this._unregisterDelayedCall(timeout);
    }
    _unregisterDelayedCall(timeout) {
        if (!this._delayedCalls) return;
        var idx = this._delayedCalls.indexOf(timeout);
        if (idx > -1) this._delayedCalls.splice(idx, 1);
    }


    /**
     * Convenience method for playing sound.
     * @param {String} id 
     * @returns {String} id for soundinstance
     **/
    playSound(id, completeHandler, errorHandler, delay, offset, loop, volume, pan, fadeInDuration) {
        if (!this._soundManager) {
            console.error("AbstractContextObject.playSound: soundManager undefined. Please inject soundManager in baseclass.")
        }
        return this._soundManager.playSound(id, completeHandler, errorHandler, delay, offset, loop, volume, pan, fadeInDuration);
    }


    /**
     * Convenience method for tracking GA events
     * Parameters label, value, dimensions, metrics, fields are optional.
     * Uses GoogleAnalyticsEvent
     **/
    track(category, action, label, value, dimensions, metrics, fields) {
        this._dispatcher.dispatch(GoogleAnalyticsEvent.EVENT, { category: category, action: action, label: label, value: value, dimensions: dimensions, metrics: metrics, fields: fields });
    }

    /**
     * 
     * @param {String} screenType 
     * @param {Object} parameters - Parameter object which will be set as skin.parameters so it can be accessed by mediator.
     */
    gotoScreen(screenType, parameters, animationType = null, animationOptions = null) {
        const skin = SkinFactory.getSkin(screenType);
        skin.parameters = parameters;
        const commandEvent = new NavigateEvent(NavigateEvent.GOTO_SCREEN_REQUEST, null, skin);
        if (animationType) {
            commandEvent.animationType = animationType;
            if (animationOptions) {
                commandEvent.animationOptions = animationOptions;
            }
        }
        this._dispatcher.dispatch(commandEvent.type, { data: commandEvent });
        return skin;
    }

    /**
     * Opens a screen
     * @param {SkinKey} skinKey 
     * @param {{ animationType: string, animationOptions: AnimationOptions }} options 
     * @param {Object} skinData
     */
    openScreen(skinKey, options, skinData) {
        options = options || {};

        const { animationType, animationOptions } = options;

        const screenSkin = SkinFactory.getSkin(skinKey);
        screenSkin.customData = skinData;
        const commandEvent = new NavigateEvent(NavigateEvent.OPEN_SCREEN_REQUEST, null, screenSkin, null, null, null, null, animationType, animationOptions);
        this._dispatcher.dispatch(commandEvent.type, { data: commandEvent });

        return screenSkin;
    }

    /**
     * Closes a screen
     * @param {SkinKey} skinKey 
     * @param {{ animationType: string, animationOptions: AnimationOptions }} options
     */
    closeScreen(screenSkin, options) {
        options = options || {};

        const { animationType, animationOptions } = options;

        const commandEvent = new NavigateEvent(NavigateEvent.CLOSE_SCREEN_REQUEST, null, screenSkin, null, null, null, null, animationType, animationOptions);
        this._dispatcher.dispatch(commandEvent.type, { data: commandEvent });
    }
}